import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useSocket } from '../lib/socket';
import { useAuthStore } from '../store/auth';
import type { Call } from '@marnic/shared';
import type { CallEndedPayload } from '@marnic/shared/events';
import { formatDuration, formatUKForDisplay } from '@marnic/shared';
import { relativeTime } from '../lib/utils';

interface CallsResponse {
  total: number;
  items: (Call & { lead: { firstName: string; lastName: string; company: string | null } | null })[];
}

export function Dashboard(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  const socket = useSocket();
  const calls = useQuery({
    queryKey: ['calls', 'recent'],
    queryFn: () => api.get<CallsResponse>('/calls?limit=15'),
  });

  useEffect(() => {
    if (!socket || !accessToken) return;
    const onCallEnded = (_p: CallEndedPayload): void => {
      void qc.invalidateQueries({ queryKey: ['calls', 'recent'] });
    };
    socket.on('call_ended', onCallEnded);
    return () => {
      socket.off('call_ended', onCallEnded);
    };
  }, [socket, accessToken, qc]);

  const today = calls.data?.items.filter((c) => {
    const d = new Date(c.startedAt);
    return d.toDateString() === new Date().toDateString();
  }) ?? [];

  const totalTalk = today.reduce((acc, c) => acc + (c.durationSeconds ?? 0), 0);
  const connected = today.filter((c) => c.answeredAt).length;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Welcome back, {user?.firstName}</h1>
        <p className="text-sm text-ink-500">Here's your day so far.</p>
      </header>
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Calls today" value={today.length} />
        <Stat label="Connected" value={`${connected}`} />
        <Stat label="Talk time" value={formatDuration(totalTalk)} />
        <Stat label="Connect rate" value={today.length ? `${Math.round((connected / today.length) * 100)}%` : '0%'} />
      </section>

      <section className="card overflow-hidden">
        <header className="flex items-center justify-between border-b border-ink-200 px-4 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Recent calls</h2>
        </header>
        <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-ink-50 text-left text-xs uppercase tracking-wider text-ink-500">
            <tr>
              <th className="px-4 py-2">When</th>
              <th className="px-4 py-2">Direction</th>
              <th className="px-4 py-2">Counterparty</th>
              <th className="px-4 py-2">Duration</th>
              <th className="px-4 py-2">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {calls.isLoading && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-500">Loading…</td></tr>
            )}
            {calls.data?.items.map((c) => (
              <tr key={c.id} className="border-t border-ink-100">
                <td className="px-4 py-2 text-ink-500">{relativeTime(c.startedAt)}</td>
                <td className="px-4 py-2">
                  <span className={`badge ${c.direction === 'outbound' ? 'bg-brand-100 text-brand-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {c.direction}
                  </span>
                </td>
                <td className="px-4 py-2">
                  {c.lead
                    ? `${c.lead.firstName} ${c.lead.lastName}${c.lead.company ? ` · ${c.lead.company}` : ''}`
                    : formatUKForDisplay(c.direction === 'outbound' ? c.toNumber : c.fromNumber)}
                </td>
                <td className="px-4 py-2 font-mono">{formatDuration(c.durationSeconds ?? 0)}</td>
                <td className="px-4 py-2">{c.disposition ?? c.status}</td>
              </tr>
            ))}
            {calls.data?.items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-ink-500">No calls yet.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }): JSX.Element {
  return (
    <div className="card p-4">
      <div className="text-xs uppercase tracking-wide text-ink-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}
